/*
 * CUSTOM (Schuhbeck): Mega Navigation
 * Self-contained companions to sections/mega-navigation.liquid.
 * - <mega-navigation>: desktop bar. Hover/fade is pure CSS; this element adds
 *   touch toggling, ESC-to-close, and clones the bar into the theme's sticky
 *   header (which only clones the header section's own rows).
 * - <mega-mobile-navigation>: drawer drill-down, modeled on the theme's
 *   MobileNavigation (assets/section-header.js) but bound at upgrade time so
 *   it works no matter when the section renders.
 */

if ( typeof MegaNavigation !== 'function' ) {

	class MegaNavigation extends HTMLElement {

		connectedCallback(){

			// touch: first tap on a branch opens its panel instead of navigating

			this.querySelectorAll(':scope > nav > .mega-nav__list > .shopify-block > .mega-item--branch').forEach(item=>{
				const link = item.querySelector(':scope > .mega-item__link');
				link.addEventListener('touchend', e=>{
					if ( ! item.classList.contains('focus') ) {
						e.preventDefault();
						this._closeAll(item);
						item.classList.add('focus');
					}
				});
			});

			document.addEventListener('keydown', e=>{
				if ( e.key === 'Escape' ) {
					this._closeAll();
					if ( this.contains(document.activeElement) ) {
						document.activeElement.blur();
					}
				}
			});
			document.addEventListener('touchend', e=>{
				if ( ! this.contains(e.target) ) {
					this._closeAll();
				}
			});

			// sticky header: append a clone of the bar (hover stays CSS-driven)

			if ( ! this.hasAttribute('data-mega-sticky-clone') ) {
				this._mountStickyClone();
			}

		}

		_closeAll(except){
			this.querySelectorAll('.mega-item.focus').forEach(elm=>{
				if ( elm !== except ) elm.classList.remove('focus');
			});
		}

		_mountStickyClone(){
			const mount = ()=>{
				const stickyHeader = document.querySelector('.sticky-header');
				if ( ! stickyHeader ) return false;
				if ( ! stickyHeader.querySelector('mega-navigation') ) {
					const clone = this.cloneNode(true);
					clone.setAttribute('data-mega-sticky-clone', '');
					stickyHeader.appendChild(clone);
				}
				return true;
			};
			// the theme creates .sticky-header when <main-header> upgrades;
			// script order isn't guaranteed, so retry briefly.
			if ( ! mount() ) {
				let tries = 0;
				const timer = setInterval(()=>{
					if ( mount() || ++tries > 20 ) clearInterval(timer);
				}, 250);
			}
		}

	}

	if ( typeof customElements.get('mega-navigation') == 'undefined' ) {
		customElements.define('mega-navigation', MegaNavigation);
	}

}

if ( typeof MegaMobileNavigation !== 'function' ) {

	class MegaMobileNavigation extends HTMLElement {

		connectedCallback(){

			this._depth = 0;
			this._drawer = this.closest('sidebar-drawer');
			// the host clips at its measured height (overflow:hidden), so any
			// layout shift after the one-shot measurement (webfont swap, lazy
			// thumbnails) would cut off the panel's bottom — re-sync on resize
			this._panelObserver = new ResizeObserver(()=>{ this._syncHeight(); });
			const eyebrow = this.dataset.eyebrow || '';

			// inject a heading (eyebrow + parent title) at the top of every child panel

			this.querySelectorAll('.mega-item__children').forEach(list=>{
				const item = list.closest('.mega-item');
				const title = item ? item.querySelector(':scope > .mega-item__link .mega-item__title') : null;
				const head = document.createElement('div');
				head.className = 'mega-drawer__panel-head';
				head.setAttribute('role', 'presentation');
				if ( eyebrow ) {
					const eyebrowElm = document.createElement('span');
					eyebrowElm.className = 'mega-drawer__panel-eyebrow';
					eyebrowElm.textContent = eyebrow;
					head.appendChild(eyebrowElm);
				}
				const titleElm = document.createElement('span');
				titleElm.className = 'mega-drawer__panel-title';
				titleElm.textContent = title ? title.textContent.trim() : '';
				head.appendChild(titleElm);
				list.prepend(head);
			});

			// branch links drill down instead of navigating

			this.querySelectorAll('.mega-item--branch > .mega-item__link').forEach(link=>{
				link.addEventListener('click', e=>{
					e.preventDefault();
					const item = link.closest('.mega-item');
					item.classList.add('opened');
					this._depth++;
					this._update();
				});
			});

			// the single back pill in the drawer header (rendered in Liquid)

			if ( this._drawer ) {
				const back = this._drawer.querySelector('[data-js-mega-back]');
				if ( back ) {
					back.addEventListener('click', ()=>{ this._back(); });
				}
			}

		}

		_back(){
			const opened = this.querySelectorAll('.mega-item.opened');
			if ( opened.length ) {
				const last = opened[opened.length - 1];
				setTimeout(()=>{ last.classList.remove('opened'); }, 301);
			}
			this._depth = Math.max(0, this._depth - 1);
			this._update();
		}

		_update(){
			this.setAttribute('data-depth', this._depth);
			if ( this._drawer ) {
				this._drawer.scrollTo({ top: 0 });
				// the header swaps logo <-> back pill and the extras hide below level 1
				this._drawer.classList.toggle('is-drilled', this._depth > 0);
			}
			this._panelObserver.disconnect();
			if ( this._depth === 0 ) {
				this.style.height = 'auto';
			} else {
				const panel = this._currentPanel();
				if ( panel ) {
					this._panelObserver.observe(panel);
					this._syncHeight();
				}
			}
		}

		_currentPanel(){
			const opened = this.querySelectorAll('.mega-item.opened');
			const current = opened[opened.length - 1];
			return current ? current.querySelector(':scope > .mega-item__children') : null;
		}

		_syncHeight(){
			if ( this._depth === 0 ) return;
			// re-resolve the panel: after going back, the leaving panel loses
			// .opened 300ms later and the observer's target may be stale
			const panel = this._currentPanel();
			if ( panel ) this.style.height = `${panel.scrollHeight}px`;
		}

	}

	if ( typeof customElements.get('mega-mobile-navigation') == 'undefined' ) {
		customElements.define('mega-mobile-navigation', MegaMobileNavigation);
	}

}
